import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Heart, Eye, Play, Plus, Search, Trash2, X, Flame, Clock } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import Navbar from "../components/Navbar";
import SEO from "../components/SEO";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { DeliverableUploader } from "../components/DeliverableUploader";
import { BrandsActiveStrip } from "../components/BrandsActiveStrip";
import { RecentDealsStrip } from "../components/ResultsWall";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const abs = (u) => (!u || u.startsWith("http") ? u : `${BACKEND_URL}${u}`);
const NICHES = ["Fashion", "Beauty", "Fitness", "Food", "Travel", "Tech", "Gaming", "Lifestyle", "Startups", "Product Reviews", "AI"];
const fmt = (n) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n || 0));

const Card = ({ p, onOpen, onLike }) => {
  const isVideo = p.file?.content_type?.startsWith("video/");
  return (
    <article className="group relative rounded-2xl overflow-hidden bg-[#0C0C10] hairline break-inside-avoid mb-4 cursor-pointer" onClick={() => onOpen(p)} data-testid={`explore-card-${p.id}`}>
      {isVideo ? (
        <video src={`${abs(p.file.url)}#t=0.1`} muted playsInline preload="metadata" className="w-full bg-black" onMouseEnter={(e) => e.currentTarget.play().catch(() => {})} onMouseLeave={(e) => { e.currentTarget.pause(); e.currentTarget.currentTime = 0.1; }} />
      ) : (
        <img src={abs(p.file.url)} alt={p.title} className="w-full object-cover" loading="lazy" />
      )}
      {isVideo && <div className="absolute top-3 right-3 h-8 w-8 rounded-full bg-black/60 backdrop-blur flex items-center justify-center opacity-80 group-hover:opacity-0 transition-opacity"><Play className="h-3.5 w-3.5 text-white ml-0.5" /></div>}
      <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/90 via-black/50 to-transparent">
        <div className="font-semibold text-sm text-white truncate">{p.title}</div>
        <div className="flex items-center justify-between mt-1.5">
          <Link to={`/creators/${p.creator_id}`} onClick={(e) => e.stopPropagation()} className="flex items-center gap-1.5 min-w-0 hover:underline" data-testid={`explore-creator-${p.id}`}>
            {p.creator_avatar ? <img src={abs(p.creator_avatar)} alt="" className="h-5 w-5 rounded-full object-cover" /> : <span className="h-5 w-5 rounded-full nexus-gradient" />}
            <span className="text-xs text-zinc-300 truncate">{p.creator_name}</span>
          </Link>
          <div className="flex items-center gap-2 text-[11px] text-zinc-300 shrink-0">
            <button type="button" onClick={(e) => { e.stopPropagation(); onLike(p); }} className={`inline-flex items-center gap-1 ${p.liked ? "text-nexus-pink" : "hover:text-white"}`} data-testid={`explore-like-${p.id}`}><Heart className={`h-3.5 w-3.5 ${p.liked ? "fill-current" : ""}`} /> {fmt(p.likes)}</button>
            <span className="inline-flex items-center gap-1"><Eye className="h-3.5 w-3.5" /> {fmt(p.views)}</span>
          </div>
        </div>
      </div>
    </article>
  );
};

const UploadDialog = ({ open, onClose, onCreated }) => {
  const [files, setFiles] = useState([]);
  const [form, setForm] = useState({ title: "", caption: "", niche: "", tags: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!files.length) return toast.error("Upload a video or image first");
    if (form.title.trim().length < 2) return toast.error("Give it a title");
    setBusy(true);
    try {
      const { data } = await api.post("/explore", { ...form, tags: form.tags.split(/[,\s]+/).filter(Boolean), file: files[0] });
      toast.success("Added to Explore · +15 XP");
      onCreated(data);
      setFiles([]); setForm({ title: "", caption: "", niche: "", tags: "" });
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0C0C10] border-white/10 text-white max-w-lg" data-testid="explore-upload-dialog">
        <DialogHeader><DialogTitle className="font-display">Share your work on Explore</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <DeliverableUploader files={files} onChange={(f) => setFiles(f.slice(-1))} disabled={busy} />
          <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title — e.g. 'Skincare GRWM for Glow Co.'" className="bg-white/[0.02] border-white/10 text-white" data-testid="explore-title-input" />
          <Textarea value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} rows={2} placeholder="What was the brief? What worked?" className="bg-white/[0.02] border-white/10 text-white" data-testid="explore-caption-input" />
          <div className="grid grid-cols-2 gap-2">
            <select value={form.niche} onChange={(e) => setForm({ ...form, niche: e.target.value })} className="h-10 rounded-md bg-[#0C0C10] border border-white/10 text-white text-sm px-2" data-testid="explore-niche-select">
              <option value="">Niche</option>
              {NICHES.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="tags: ugc, unboxing" className="bg-white/[0.02] border-white/10 text-white" data-testid="explore-tags-input" />
          </div>
          <Button onClick={submit} disabled={busy} className="w-full nexus-gradient text-white rounded-full border-0 font-semibold" data-testid="explore-submit-btn">Publish to Explore</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Viewer = ({ p, me, onClose, onLike, onDelete }) => {
  if (!p) return null;
  const isVideo = p.file?.content_type?.startsWith("video/");
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0C0C10] border-white/10 text-white max-w-3xl p-0 overflow-hidden" data-testid="explore-viewer">
        <div className="grid md:grid-cols-[1.4fr_1fr]">
          <div className="bg-black flex items-center">
            {isVideo ? <video src={abs(p.file.url)} controls autoPlay playsInline className="w-full max-h-[70vh]" data-testid="explore-viewer-video" /> : <img src={abs(p.file.url)} alt={p.title} className="w-full max-h-[70vh] object-contain" />}
          </div>
          <div className="p-5 space-y-4">
            <div>
              <h3 className="font-display font-bold text-xl leading-tight" data-testid="explore-viewer-title">{p.title}</h3>
              {p.caption && <p className="text-sm text-zinc-400 mt-2 whitespace-pre-wrap">{p.caption}</p>}
              {(p.niche || p.tags?.length) ? <div className="flex flex-wrap gap-1.5 mt-3">{p.niche && <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded bg-nexus-blue/15 text-nexus-blue">{p.niche}</span>}{p.tags?.map((t) => <span key={t} className="text-[10px] px-2 py-1 rounded bg-white/5 text-zinc-400">#{t}</span>)}</div> : null}
            </div>
            <Link to={`/creators/${p.creator_id}`} className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/5 hover:border-white/20" data-testid="explore-viewer-creator">
              {p.creator_avatar ? <img src={abs(p.creator_avatar)} alt="" className="h-10 w-10 rounded-full object-cover" /> : <span className="h-10 w-10 rounded-full nexus-gradient" />}
              <div className="min-w-0"><div className="font-semibold text-sm truncate">{p.creator_name}</div><div className="text-[11px] text-zinc-500 capitalize">{p.creator_tier} · {fmt(p.creator_followers)} followers</div></div>
            </Link>
            <div className="flex items-center gap-3 text-sm">
              <button type="button" onClick={() => onLike(p)} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border ${p.liked ? "border-nexus-pink text-nexus-pink" : "border-white/15 text-zinc-300 hover:text-white"}`} data-testid="explore-viewer-like"><Heart className={`h-4 w-4 ${p.liked ? "fill-current" : ""}`} /> {p.likes}</button>
              <span className="inline-flex items-center gap-1.5 text-zinc-500"><Eye className="h-4 w-4" /> {p.views}</span>
              {me?.id === p.creator_id && <button type="button" onClick={() => onDelete(p)} className="ml-auto inline-flex items-center gap-1 text-xs text-red-300 hover:text-red-100" data-testid="explore-viewer-delete"><Trash2 className="h-3.5 w-3.5" /> Remove</button>}
            </div>
            {me?.role === "brand" && <Button asChild className="w-full nexus-gradient text-white rounded-full border-0 font-semibold"><Link to={`/creators/${p.creator_id}`} data-testid="explore-viewer-hire">Hire {p.creator_name?.split(" ")[0]} for something like this</Link></Button>}
            {!me && <Button asChild variant="outline" className="w-full rounded-full border-white/15 text-white hover:bg-white/10 hover:text-white"><Link to="/auth">Sign in to like & hire</Link></Button>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function Explore() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const niche = params.get("niche") || "";
  const sort = params.get("sort") || "trending";
  const [q, setQ] = useState("");
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get("/explore", { params: { niche, sort, q, limit: 36 } }).then((r) => { setPosts(r.data.posts); setTotal(r.data.total); }).catch(() => {}).finally(() => setLoading(false));
  }, [niche, sort, q]);

  const like = async (p) => {
    if (!user) return toast.info("Sign in to like posts");
    try {
      const { data } = await api.post(`/explore/${p.id}/like`);
      const upd = (x) => (x.id === p.id ? { ...x, liked: data.liked, likes: data.likes } : x);
      setPosts((arr) => arr.map(upd));
      setOpen((o) => (o && o.id === p.id ? upd(o) : o));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const openPost = async (p) => {
    setOpen(p);
    try { const { data } = await api.get(`/explore/${p.id}`); setOpen(data); setPosts((arr) => arr.map((x) => (x.id === p.id ? { ...x, views: data.views } : x))); } catch {}
  };
  const remove = async (p) => {
    await api.delete(`/explore/${p.id}`);
    setPosts((arr) => arr.filter((x) => x.id !== p.id)); setOpen(null); toast.success("Removed");
  };
  const setParam = (k, v) => { const n = new URLSearchParams(params); v ? n.set(k, v) : n.delete(k); setParams(n, { replace: true }); };
  const columns = useMemo(() => posts, [posts]);

  return (
    <div className="min-h-screen grain">
      <SEO title="Explore creator work | Nexus" description="Browse real videos UK creators have made for brands. Get inspired, find your next creator, or share your best work." path="/explore" />
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-2">Explore</div>
            <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tighter">Work creators are proud of.</h1>
            <p className="text-zinc-400 mt-2 max-w-xl text-sm sm:text-base">Real videos made for real briefs. Brands: find your next creator. Creators: see what's landing and share your own.</p>
          </div>
          {user?.role === "creator" && <Button onClick={() => setUploadOpen(true)} className="nexus-gradient text-white rounded-full border-0 font-semibold" data-testid="explore-upload-btn"><Plus className="h-4 w-4 mr-1.5" /> Share your work</Button>}
        </div>

        <BrandsActiveStrip />
        <RecentDealsStrip />

        <div className="flex flex-wrap items-center gap-2" data-testid="explore-filters">
          <div className="inline-flex rounded-full bg-white/5 p-1">
            {[["trending", Flame, "Trending"], ["new", Clock, "Newest"]].map(([k, I, label]) => (
              <button key={k} type="button" onClick={() => setParam("sort", k === "trending" ? "" : k)} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${sort === k ? "bg-white text-black" : "text-zinc-400 hover:text-white"}`} data-testid={`explore-sort-${k}`}><I className="h-3.5 w-3.5" /> {label}</button>
            ))}
          </div>
          <div className="flex gap-1.5 overflow-x-auto no-scrollbar">
            <button type="button" onClick={() => setParam("niche", "")} className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap ${!niche ? "bg-nexus-pink/15 text-nexus-pink border border-nexus-pink/30" : "bg-white/5 text-zinc-400 hover:text-white"}`} data-testid="explore-niche-all">All</button>
            {NICHES.map((n) => <button key={n} type="button" onClick={() => setParam("niche", n)} className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap ${niche === n ? "bg-nexus-pink/15 text-nexus-pink border border-nexus-pink/30" : "bg-white/5 text-zinc-400 hover:text-white"}`} data-testid={`explore-niche-${n.toLowerCase().replace(/\s/g, "-")}`}>{n}</button>)}
          </div>
          <div className="relative ml-auto w-full sm:w-56">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search titles & tags" className="pl-9 h-9 bg-white/[0.02] border-white/10 text-white" data-testid="explore-search-input" />
            {q && <button type="button" onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"><X className="h-4 w-4" /></button>}
          </div>
        </div>

        {loading ? (
          <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4">{Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-64 rounded-2xl bg-white/[0.03] animate-pulse mb-4 break-inside-avoid" />)}</div>
        ) : columns.length === 0 ? (
          <div className="text-center py-24 rounded-3xl hairline bg-[#0C0C10]" data-testid="explore-empty">
            <div className="font-display text-2xl mb-2">Nothing here yet</div>
            <p className="text-zinc-500 text-sm">{user?.role === "creator" ? "Be the first — share a video you're proud of." : "Creators are uploading their best work. Check back soon."}</p>
          </div>
        ) : (
          <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4" data-testid="explore-grid">{columns.map((p) => <Card key={p.id} p={p} onOpen={openPost} onLike={like} />)}</div>
        )}
        <div className="text-xs text-zinc-600">{total} post{total === 1 ? "" : "s"}</div>
      </div>
      <UploadDialog open={uploadOpen} onClose={() => setUploadOpen(false)} onCreated={(p) => setPosts((arr) => [p, ...arr])} />
      <Viewer p={open} me={user} onClose={() => setOpen(null)} onLike={like} onDelete={remove} />
    </div>
  );
}
